import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMsal } from "@azure/msal-react"; // Import MSAL hook
import { loginRequest } from "../auth/msalConfig"; // Import MSAL scopes
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "../components/ui/use-toast";
import axios from "axios";
import { Separator } from "../components/ui/separator"; // Import Separator
import { UserPlus, LogIn } from "lucide-react"; // Import icons

export default function SignupPage() {
  const { login, handleMicrosoftLoginCallback } = useAuth(); // Get MSAL callback handler
  const { instance } = useMsal(); // Get MSAL instance

  const [form, setForm] = useState({
    username: "",
    password: "",
    full_name: "",
    email: "",
    role: "BM",
  });
  const [loading, setLoading] = useState(false);
  const [msalLoading, setMsalLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Traditional Signup Handler
  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post("/signup", form);
      // After successful signup, log the user in automatically
      await login({ token: res.data.access_token });
      toast({
        title: "Signup success!",
        description: "Account created and logged in.",
      });
      navigate("/"); // Navigate to home after signup
    } catch (err) {
      toast({
        title: "Signup failed",
        description: err.response?.data?.error || err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  // Microsoft Login/Signup Handler
  const handleMicrosoftLogin = async () => {
    setMsalLoading(true);
    try {
      const response = await instance.loginPopup(loginRequest);
      console.log("MSAL loginPopup response on signup page:", response);
      // Set the active account before sending to backend
      if (response.account) {
        instance.setActiveAccount(response.account);
      }
      // Send ID token to backend - backend will create user if not exists
      await handleMicrosoftLoginCallback(response.idToken, response.account);
      toast({
        title: "Success",
        description: `Logged in/Signed up with Microsoft as ${response.account?.name || "User"}!`, // Use name from MSAL account
      });
      navigate("/"); // Redirect after backend confirms/creates user
    } catch (error) {
      console.error("Microsoft Login/Signup failed:", error);
      toast({
        title: "Microsoft Login/Signup Failed",
        description: error.message || "Could not process Microsoft sign-in.",
        variant: "destructive",
      });
    } finally {
      setMsalLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create Account</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Microsoft Login/Signup Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleMicrosoftLogin}
              disabled={msalLoading || loading}
            >
              {msalLoading ? (
                "Processing..."
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" /> Sign Up / Login with
                  Microsoft
                </>
              )}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or sign up with username/password
                </span>
              </div>
            </div>

            {/* Traditional Signup Form */}
            <form className="space-y-4" onSubmit={handleSubmit}>
              <Input
                name="username"
                required
                minLength={3}
                maxLength={32}
                placeholder="Username"
                value={form.username}
                onChange={handleChange}
                disabled={msalLoading || loading}
              />
              <Input
                name="email"
                required
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange}
                disabled={msalLoading || loading}
              />
              <Input
                name="full_name"
                required
                placeholder="Full Name"
                value={form.full_name}
                onChange={handleChange}
                disabled={msalLoading || loading}
              />
              <Input
                name="password"
                type="password"
                required
                minLength={8}
                placeholder="Password (min 8 chars)"
                value={form.password}
                onChange={handleChange}
                disabled={msalLoading || loading}
              />
              <select
                name="role"
                required
                className="flex w-full h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                value={form.role}
                onChange={handleChange}
                disabled={msalLoading || loading}
              >
                <option value="BM">Business Manager (BM)</option>
                <option value="RSM">RSM (Field Rep)</option>
              </select>
              <Button
                type="submit"
                className="w-full"
                disabled={loading || msalLoading}
              >
                {loading ? "Creating..." : "Sign Up with Password"}
              </Button>
            </form>
          </div>
          <div className="mt-6 text-center">
            <Link className="text-sm text-primary hover:underline" to="/login">
              Already have an account? Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
